/**
 * SESSION-SCOPED GENERATION LEDGER (S4/S5).
 *
 * The S5 picker must show ONLY the avatars produced in THIS card-design
 * attempt for THIS style — not every avatar the account ever made, and not
 * other styles. Server-side we can only scope by style + recency (see
 * listGenerationsForPick); the notion of "this session" lives in the tab.
 *
 * sessionStorage is the right store: it survives in-tab reloads and
 * back/forward navigation (so back→forward RESUMES the set), and it dies with
 * the tab, so a later visit starts a clean attempt instead of resurrecting old
 * avatars.
 *
 * The ledger holds generation IDs only — no image URLs, no PII.
 */

const PREFIX = "cm:gen-session:v1";

type Triple = { styleId: string; subStyleId?: string | null; themeId?: string | null };

function key(programSlug: string, t: Triple): string {
  return [PREFIX, programSlug, t.styleId, t.subStyleId ?? "-", t.themeId ?? "-", readOrderEpoch(programSlug)].join(":");
}

function safeRead(k: string): string[] {
  try {
    const raw = sessionStorage.getItem(k);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === "string") : [];
  } catch {
    return [];
  }
}

/** IDs generated during this tab session for this program + style pick. */
export function readSessionGenerationIds(programSlug: string, triple: Triple): string[] {
  return safeRead(key(programSlug, triple));
}

/** Append IDs (deduped, order preserved) to this session's ledger. */
export function rememberSessionGenerations(
  programSlug: string,
  triple: Triple,
  ids: string[],
): string[] {
  const k = key(programSlug, triple);
  const merged = Array.from(new Set([...safeRead(k), ...ids.filter(Boolean)]));
  try {
    sessionStorage.setItem(k, JSON.stringify(merged));
  } catch {
    /* private mode / quota — scoping degrades to style+recency, never throws */
  }
  return merged;
}

/* ── ORDER SCOPE ──────────────────────────────────────────────────────────────
 * The ledger used to be keyed by program + style only. That silently made a
 * REPEAT order of the SAME style in the SAME tab inherit the previous order's
 * generation IDs — the "ordered another card and got 4 avatars (2 of them from
 * last time, still raw)" report. An order epoch scopes the ledger to ONE order
 * attempt: starting a new order bumps it, so the new order begins empty.
 */
const EPOCH = (programSlug: string) => `${PREFIX}:epoch:${programSlug}`;

export function readOrderEpoch(programSlug: string): string {
  try {
    const cur = sessionStorage.getItem(EPOCH(programSlug));
    if (cur) return cur;
    const next = String(Date.now());
    sessionStorage.setItem(EPOCH(programSlug), next);
    return next;
  } catch {
    return "0";
  }
}

/** Begin a fresh order attempt: the ledger for every style starts empty. */
export function startNewOrderEpoch(programSlug: string): string {
  const next = String(Date.now());
  try {
    // Drop this tab's ledgers for the program so nothing can be resurrected.
    for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(`${PREFIX}:${programSlug}:`)) sessionStorage.removeItem(k);
    }
    sessionStorage.setItem(EPOCH(programSlug), next);
  } catch {
    /* storage unavailable — scoping degrades to style+recency */
  }
  return next;
}
